import { useCallback, useEffect, useMemo, useState } from 'react'

// Tiny path-based router for the portal. Three meaningful routes plus the
// auth-callback bounce target:
//   /              — embed page (any signed-in user)
//   /distribution  — approvals/distribution editor (user+)
//   /login         — magic-link request screen
//   /auth/callback — Supabase redirect target after the user clicks the email
//
// Stability matters: consumers depend on the returned `route` object in
// useEffect deps, so we memoize navigate (useCallback) and the returned
// shape (useMemo) so reference equality holds across renders that don't
// change `path`. Without this, effects loop and pages "freak out".

export interface RouteApi {
  path: string
  navigate: (to: string, replace?: boolean) => void
}

function currentPath(): string {
  if (typeof window === 'undefined')
    return '/'
  return window.location.pathname
}

export function useRoute(): RouteApi {
  const [path, setPath] = useState(() => currentPath())

  useEffect(() => {
    const onPop = () => setPath(currentPath())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const navigate = useCallback((to: string, replace = false) => {
    if (to === window.location.pathname)
      return
    if (replace) {
      window.history.replaceState({}, '', to)
    }
    else {
      window.history.pushState({}, '', to)
    }
    setPath(to)
  }, [])

  return useMemo(() => ({ path, navigate }), [path, navigate])
}
